/** Possible email categorization outcomes */
export type EmailCategory = 'do_nothing' | 'delete' | 'archive' | 'unsubscribe';

/** Configuration options for LLM providers */
export interface LLMConfig {
  /** API key for the LLM provider */
  apiKey: string;
  /** Model identifier (defaults to provider's defaultModel if not specified) */
  model?: string;
  /** Temperature setting for response randomness (0-1, defaults to 0.1) */
  temperature?: number;
}

/** Common interface for all LLM providers */
export interface LLMProvider {
  /**
   * Analyzes an email and returns structured categorization
   * @param emailContent - Email details including subject, sender, and content
   * @returns Promise containing detailed email analysis
   */
  analyzeEmail(emailContent: {
    subject: string;
    sender: string;
    date: string;
    content?: string;
  }): Promise<EmailAnalysis>;
}

/** Structured analysis of an email */
export interface EmailAnalysis {
  /** Primary categorization of the email */
  category: EmailCategory;
  /** Optional subcategory for more specific classification */
  subcategory?: string;
  /** Importance level of the email */
  priority: 'important' | 'normal' | 'low';
  /** Confidence score of the analysis (0-1) */
  confidence: number;
  /** Whether the email requires action */
  action_needed: boolean;
  /** Type of action required if any */
  action_type?: string;
  /** ISO date string for action deadline if any */
  deadline?: string | null;
  /** Recommended retention period */
  retention_period?: string | null;
  /** Additional contextual information */
  context?: string;
  /** Explanation for the categorization */
  reason: string;
  /** Labels describing email content (1-3 labels) */
  labels: string[];
}

export interface LLMResponse {
  category: EmailCategory;
  subcategory?: string;
  priority: string;
  confidence?: number;
  action_needed: boolean;
  action_type?: string;
  deadline?: string | null;
  retention_period?: string | null;
  context?: string;
  reason: string;
  labels: string[];
}

export interface BaseProviderResponse {
  choices: {
    message: {
      content: string;
    };
  }[];
  usage?: {
    total_tokens: number;
  };
}

/**
 * Get labels from analysis (deduplicated)
 */
export function getAnalysisLabels(analysis: EmailAnalysis | null | undefined): string[] {
  if (!analysis) return [];
  const legacyLabel = (analysis as { label?: unknown }).label;
  const rawLabels = (analysis.labels && analysis.labels.length > 0)
    ? analysis.labels
    : (typeof legacyLabel === 'string' && legacyLabel.trim().length > 0 ? [legacyLabel.trim()] : []);
  if (rawLabels.length === 0) return [];
  // Deduplicate labels (case-insensitive)
  const seen = new Set<string>();
  return rawLabels.filter(label => {
    const key = label.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Get primary label from analysis (first label)
 */
export function getPrimaryLabel(analysis: EmailAnalysis | null | undefined): string | undefined {
  const labels = getAnalysisLabels(analysis);
  return labels[0];
}
